// AuthService.js — reusable Firebase Auth + Firestore operations
// Import auth and db from firebase.js so everything uses the same app instance

import { initializeApp, getApp, deleteApp } from "firebase/app";

import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  verifyPasswordResetCode,
  confirmPasswordReset,
  applyActionCode,
  updatePassword,
  updateEmail,
  verifyBeforeUpdateEmail,
  reauthenticateWithCredential,
  EmailAuthProvider,
  getAuth,
} from "firebase/auth";

import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  collection,
  query,
  where,
  orderBy,
  getDocs,
  addDoc,
  serverTimestamp,
  runTransaction,
  Timestamp,
} from "firebase/firestore";

import { httpsCallable } from "firebase/functions";

import { auth, db, functions } from "./firebase";
import { normalizeScope } from "./affiliationService";

// ─────────────────────────────────────────────────────────────────────────────
// INITIAL URL ACTION PARAMS — snapshotted at module load
// ─────────────────────────────────────────────────────────────────────────────

// Firebase action links (?mode=verifyEmail&oobCode=...) land directly on our
// OWN pages because this project has a custom Action URL configured — see
// applyEmailVerification's docstring below. That makes those query params
// fragile: anything that rewrites the URL before the target screen mounts
// (this app's hand-rolled screen-state routing, a hosting redirect, an apex
// vs. www normalisation) silently destroys them, and the action code then
// never gets applied even though the user did click the link.
//
// ES modules are all evaluated before any component renders, so reading
// window.location here is the earliest point in the app's lifetime we can
// reliably capture them. Screens must read this instead of touching
// window.location.search themselves.
//
// Caveat: this only holds while AuthService is statically imported. If it is
// ever moved behind a dynamic import() that resolves after routing runs, the
// snapshot stops being early enough — move this into the app entry file
// (main.jsx) at that point.
const _initialParams = new URLSearchParams(
  typeof window !== "undefined" ? window.location.search : ""
);

/**
 * The Firebase email-action params the app was originally opened with.
 *
 * @returns {{ mode: string|null, oobCode: string|null }}
 */
export const getInitialAuthAction = () => ({
  mode:    _initialParams.get("mode"),
  oobCode: _initialParams.get("oobCode"),
});

/**
 * The exact message thrown by signIn when a company account exists and is
 * approved but has never had its email verified.
 *
 * Exported so SignInScreen can recognise this one failure and offer the
 * "Resend activation link" action beside it. Matching on an exported
 * constant rather than on the message text means rewording the copy here
 * can't silently break that.
 */
export const NOT_ACTIVATED_MESSAGE =
  "Please activate your account first. Check your email for the activation link we sent when your registration was approved.";

// ─────────────────────────────────────────────────────────────────────────────
// COMPANY — Sign Up (Step 2 calls this after Cloudinary uploads)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Registers a new company.
 * 1. Creates a Firebase Auth user.
 * 2. Writes the company profile to Firestore with status "pending".
 *
 * @param {object} step1Data  — { email, password, companyName, industry, departments, deptSelections, location }
 * @param {string[]} verificationDocs — array of Cloudinary secure_url strings
 * @returns {Promise<string>} the new user's UID
 */
export const registerCompany = async (step1Data, verificationDocs) => {
  const { email: rawEmail, password, companyName, industry, departments, deptSelections, location } = step1Data;
  const email = rawEmail.trim().toLowerCase(); // normalize so Auth + Firestore always match

  // 1. Firebase Auth
  const { user } = await createUserWithEmailAndPassword(auth, email, password);

  // 2. Firestore company doc
  await setDoc(doc(db, "companies", user.uid), {
    uid:              user.uid,
    email,
    companyName,
    industry,
    // Full college names, e.g. ["College of Computer Studies"] — must match
    // the `department` value inside coordinators' own `deptSelections`
    // entries (see CoordinatorAccountProfileScreen.jsx), since that's what
    // CoordinatorCompanyListScreen.jsx matches against to route this
    // registration to the right coordinator(s). Kept flat (rather than
    // nested inside deptSelections below) purely so Firestore can query it
    // with "array-contains-any" — nested array-of-maps fields can't be
    // queried by sub-field.
    departments:      departments || [],
    // Full { department, program } pairs — the same shape coordinators save
    // on their own profile. CoordinatorCompanyListScreen.jsx uses the flat
    // `departments` array above for the initial Firestore query, then
    // refines against this array client-side so a coordinator who has
    // narrowed their own assignment down to a specific Program only sees
    // companies registered under that Program (not the whole Department).
    // Each entry also carries its OWN "pending"/"approved"/"rejected"
    // status — see approveCompanyDepartment/rejectCompanyDepartment below —
    // so a multi-department company must be approved separately by each
    // Department's coordinator before it can post/pull students there.
    // requestedAt + history match the shape affiliationService.js keeps for
    // programs added later, so every entry has the same audit trail.
    deptSelections:   (deptSelections || []).map(s => ({ ...s, status: "pending", requestedAt: Timestamp.now(), history: [{ status: "pending", at: Timestamp.now(), by: "" }] })),
    location,         // { fullAddress, region, province, city, barangay, street, lat, lng }
    verificationDocs, // Cloudinary URLs
    role:             "company",
    status:           "pending",  // coordinator must approve before sign-in
    createdAt:        serverTimestamp(),
  });

  return user.uid;
};

// ─────────────────────────────────────────────────────────────────────────────
// SIGN IN — role-aware, status-checked
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Signs in a user and verifies their role + approval status in Firestore.
 *
 * @param {"coordinator"|"student"|"company"} role
 * @param {string} emailOrStudentId — email for coordinator/company; studentId for student
 * @param {string} password
 * @returns {Promise<{ user: FirebaseUser, userData: object }>}
 * @throws descriptive Error messages safe to show in the UI
 */
// True when a company's timed suspension has run out. Read-only — the actual
// status flip (checkAndReactivateCompany) needs the company to be signed in,
// because the Firestore rules only let a company update its own document.
const isSuspensionExpired = (data) => {
  if (!data || data.status !== "suspended" || !data.suspendedUntil) return false;
  const expiry = typeof data.suspendedUntil.toMillis === "function"
    ? data.suspendedUntil.toMillis()
    : new Date(data.suspendedUntil).getTime();
  return Number.isFinite(expiry) && Date.now() >= expiry;
};

// Works out WHICH kind of account a sign-in identifier belongs to, so the sign-in
// screen doesn't have to ask "Sign in as...":
//   • only digits                → a Student ID            → "student"
//   • an email in companies      → a company account       → "company"
//   • an email in coordinators   → a coordinator           → "coordinator"
//   • any other email            → an administrator        → "admin"
// companies and coordinators are public-readable (see firestore.rules), so this works
// before anyone is signed in; admins/{uid} is NOT, so an admin is whatever is left, and
// signInAsAdmin() proves it after the password check. A promoted coordinator (who has both)
// is found in coordinators and lands on the Coordinator dashboard; the dashboard switcher
// takes them to Admin. This only PICKS the sign-in path: the password and the account's own
// role/status are always verified afterwards, so a wrong guess can't open the wrong dashboard.
export const detectSignInRole = async (identifier) => {
  const id = String(identifier || "").trim();
  if (/^\d+$/.test(id)) return "student";
  const email = id.toLowerCase();
  const byEmail = (col) => getDocs(query(collection(db, col), where("email", "==", email)));
  if (!(await byEmail("companies")).empty) return "company";
  if (!(await byEmail("coordinators")).empty) return "coordinator";
  return "admin";
};

// Administrator sign-in (admins/{uid}). Same Firebase Auth email + password as everyone else;
// the only difference is which Firestore doc proves the role. Resolves to the same
// { user, userData } shape as signIn(), with userData.role === "admin".
const signInAsAdmin = async (identifier, password) => {
  const email = String(identifier || "").trim().toLowerCase();
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    throw new Error("Please enter a valid email address.");
  }

  let cred;
  try {
    cred = await signInWithEmailAndPassword(auth, email, password);
  } catch (err) {
    if (err.code === "auth/user-not-found" || err.code === "auth/wrong-password" || err.code === "auth/invalid-credential") {
      throw new Error("Invalid credentials. Please check and try again.");
    }
    if (err.code === "auth/invalid-email") throw new Error("Please enter a valid email address.");
    throw err;
  }

  const { user } = cred;
  const adminSnap = await getDoc(doc(db, "admins", user.uid));
  const isActiveAdmin = adminSnap.exists() && adminSnap.get("role") === "admin" && adminSnap.get("status") === "active";
  if (isActiveAdmin) return { user, userData: { ...adminSnap.data(), uid: user.uid, role: "admin" } };

  // Not an admin. If it is a coordinator whose Firestore email had drifted from their Auth email
  // (so the pre-sign-in lookup missed them), hand over to the normal coordinator sign-in.
  const coordSnap = await getDoc(doc(db, "coordinators", user.uid));
  await signOut(auth);
  if (coordSnap.exists() && coordSnap.get("role") === "coordinator") {
    return signIn("coordinator", email, password);
  }
  throw new Error("Invalid credentials. Please check and try again.");
};

export const signInAuto = async (identifier, password) => {
  const role = await detectSignInRole(identifier);
  return role === "admin" ? signInAsAdmin(identifier, password) : signIn(role, identifier, password);
};

export const signIn = async (role, emailOrStudentId, password) => {
  const collectionMap = {
    coordinator: "coordinators",
    student:     "students",
    company:     "companies",
  };

  let loginEmail = emailOrStudentId;

  // Students log in with student ID → resolve their email first
  if (role === "student") {
    const q    = query(collection(db, "students"), where("studentId", "==", emailOrStudentId.trim()));
    const snap = await getDocs(q);
    if (snap.empty) throw new Error("Student ID not found. Please check and try again.");
    const studentData = snap.docs[0].data();

    // Archived students keep their Firebase Auth account and all their records —
    // they just can't sign in as an active student. Checked here, before the Auth
    // call, so no session is ever created for an archived account.
    if (studentData.isArchived === true) {
      throw new Error("Your student account has been archived. Please contact your coordinator for assistance.");
    }

    loginEmail = studentData.email;
  }

  // Coordinators and companies sign in with an EMAIL. If what was typed isn't
  // email-shaped (most often a Student ID entered on the wrong role tab), stop
  // here with a readable message instead of letting Firebase Auth reject it
  // with a raw "auth/invalid-email" error.
  if (role !== "student" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(loginEmail || "").trim())) {
    throw new Error(
      /^[\d-]+$/.test(String(loginEmail || "").trim())
        ? "That looks like a Student ID. Please select the Student role to sign in with it."
        : "Please enter a valid email address."
    );
  }

  // For companies — check Firestore status BEFORE attempting Auth sign-in
  // This prevents pending/rejected/suspended/blocked companies from logging in
  if (role === "company") {
    const q    = query(collection(db, "companies"), where("email", "==", loginEmail.trim().toLowerCase()));
    const snap = await getDocs(q);
    if (!snap.empty) {
      const companyDoc = snap.docs[0];
      const companyData = companyDoc.data();

      // A timed suspension may already be over. It used to be flipped back to
      // "approved" right here — but this runs BEFORE Firebase Auth sign-in, and
      // the rules only let a company write its own doc once signed in, so the
      // write failed with "Missing or insufficient permissions" and blocked the
      // login entirely. Now an expired suspension is simply let through here,
      // and the actual reactivation happens after sign-in below.
      const status = isSuspensionExpired(companyData) ? "approved" : companyData.status;

      if (status === "pending") {
        throw new Error("Your account is pending coordinator approval. Please wait before signing in.");
      }
      if (status === "rejected") {
        throw new Error("Your account registration was rejected. Please contact the administrator.");
      }
      if (status === "suspended") {
        throw new Error("Your company account has been suspended. Please contact the system administrator for more information.");
      }
      if (status === "blocked") {
        throw new Error("Your company account has been blocked. Please contact the system administrator for more information.");
      }
    }
  }

  // Firebase Auth sign-in
  let userCredential;
  try {
    userCredential = await signInWithEmailAndPassword(auth, loginEmail.trim().toLowerCase(), password);
  } catch (err) {
    if (
      err.code === "auth/user-not-found"    ||
      err.code === "auth/wrong-password"    ||
      err.code === "auth/invalid-credential"
    ) {
     throw new Error("Invalid credentials. Please check and try again.");
    }
    if (err.code === "auth/invalid-email") {
      throw new Error("Please enter a valid email address.");
    }
    throw err;
  }

  const { user } = userCredential;

  // Fetch Firestore profile
  const userSnap = await getDoc(doc(db, collectionMap[role], user.uid));
  if (!userSnap.exists()) {
    await signOut(auth);
    throw new Error("Invalid Credentials. Please check and try again.");
  }

  const userData = userSnap.data();

  // Reconcile Firestore's `email` field with the ACTUAL Firebase Auth email
  // whenever they've drifted apart, for companies and coordinators. This is
  // deliberately unconditional — not just "when it matches a stashed
  // pendingEmail" — because a change made via requestUserEmailChange below
  // is a two-step process: (1) Firebase sends a verification link and only
  // updates the real Auth email once it's clicked, entirely outside this
  // app, with no callback we can hook; (2) this app tries to track that
  // in-progress state in Firestore's `pendingEmail` field so the UI can show
  // "confirmation pending." If step 2's Firestore write ever fails for any
  // reason (e.g. security rules) after step 1 already succeeded, the old
  // narrower check (which required `pendingEmail` to exist and match) would
  // never fire again — permanently stranding the Firestore record on the
  // old email even though sign-in with the new one keeps succeeding. Since
  // we only reach this point after a real Firebase Auth sign-in already
  // succeeded, `user.email` is trustworthy as the source of truth — so any
  // mismatch here just gets synced, self-healing regardless of whether
  // `pendingEmail` was ever recorded.
  if ((role === "company" || role === "coordinator") && user.email
      && userData.email && userData.email.toLowerCase() !== user.email.toLowerCase()) {
    await updateDoc(doc(db, collectionMap[role], user.uid), {
      email:        user.email,
      pendingEmail: null,
    });
    userData.email = user.email;
    delete userData.pendingEmail;
  }

  // Role mismatch check
  if (userData.role !== role) {
    await signOut(auth);
    const label = role === "coordinator" ? "OJT Coordinator" : role.charAt(0).toUpperCase() + role.slice(1);
    throw new Error(`This account is not registered as a ${label}.`);  }

  // Expired suspension → reactivate now that the company is signed in (the
  // rules allow exactly this one status change from the company itself).
  if (role === "company" && isSuspensionExpired(userData)) {
    try {
      const result = await checkAndReactivateCompany(user.uid);
      if (result?.reactivated) {
        userData.status = "approved";
        userData.suspendedUntil = null;
        userData.statusReason = "Suspension period ended — auto-reactivated.";
      }
    } catch (err) {
      console.error("Auto-reactivation after an expired suspension failed:", err);
      await signOut(auth);
      throw new Error("Your suspension has ended, but we couldn't reactivate your account. Please try again in a moment or contact the system administrator.");
    }
  }

  // Approval / account-standing status check — enforced here (server-checked,
  // post-Auth) regardless of role, as a second line of defense behind the
  // pre-Auth company check above. This is what actually prevents a
  // suspended/blocked account from bypassing the restriction by refreshing
  // or logging in again: every sign-in re-reads this field from Firestore.
  if (userData.status === "pending") {
    await signOut(auth);
    throw new Error("Your account is pending admin approval. Please try again later.");
  }
  if (userData.status === "rejected") {
    await signOut(auth);
    throw new Error("Your account registration was rejected. Please contact the administrator.");
  }
  if (userData.status === "transferred") {
    await signOut(auth);
    throw new Error("This account has been transferred and no longer has access. Please contact your administrator.");
  }
  if (userData.status === "suspended") {
    await signOut(auth);
    throw new Error("Your company account has been suspended. Please contact the system administrator for more information.");
  }
  if (userData.status === "blocked") {
    await signOut(auth);
    throw new Error("Your company account has been blocked. Please contact the system administrator for more information.");
  }

  // Email-activation check — companies only. sendApprovalEmail (functions/
  // index.js) generates a Firebase "verify your email" link and puts it in
  // the approval email as the "Activate Account & Sign In" button; clicking
  // it sets `emailVerified: true` on this same Auth account. Checked here,
  // AFTER the status checks above, so a pending/rejected/suspended/blocked
  // company still gets its own specific message instead of a misleading
  // "please activate" — `emailVerified` is false for those too since they
  // never received an approval email to click. `user.emailVerified` is
  // fresh as of the signInWithEmailAndPassword call above, so no reload()
  // is needed.
  if (role === "company" && !user.emailVerified) {
    await signOut(auth);
    throw new Error(NOT_ACTIVATED_MESSAGE);
  }

  return { user, userData };
};

// ─────────────────────────────────────────────────────────────────────────────
// SIGN OUT
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Signs out the current user.
 */
export const logOut = () => signOut(auth);

// ─────────────────────────────────────────────────────────────────────────────
// EMAIL VERIFICATION — Applying the company activation link
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Applies a Firebase email-verification action code (the `oobCode` query
 * param on the "Activate" link from sendApprovalEmail, functions/index.js).
 *
 * This project's Firebase Auth project has a custom Action URL configured
 * (same reason the password-reset flow above was rewritten to skip Firebase's
 * email links entirely — see resetPasswordInApp's docstring) — so clicking a
 * Firebase action link lands DIRECTLY on our own continueUrl (ojtern.com/signin)
 * with `?mode=verifyEmail&oobCode=...` in the query string, and Firebase's own
 * hosted confirmation page — which would otherwise call applyActionCode
 * automatically — never runs. Without this function, the link "worked" in the
 * sense that it redirected somewhere, but `emailVerified` was never actually
 * set to true, which is exactly why sign-in kept showing "please activate"
 * even after clicking it.
 *
 * Call this on mount wherever `mode=verifyEmail` can land (SignInScreen, since
 * that's the continueUrl) — see SignInScreen.jsx's useEffect.
 *
 * @param {string} oobCode — from the `oobCode` query param
 * @returns {Promise<void>}
 * @throws descriptive Error (safe to show in UI) — e.g. the link already used,
 *   expired, or invalid
 */
export const applyEmailVerification = async (oobCode) => {
  try {
    await applyActionCode(auth, oobCode);
  } catch (err) {
    if (err.code === "auth/invalid-action-code") {
      throw new Error("This activation link has already been used or has expired. Use 'Resend activation link' below to get a fresh one — or just try signing in, since your account may already be activated.");
    }
    throw new Error(err.message || "Failed to activate your account. Please try again or contact support.");
  }

  // applyActionCode changes emailVerified on the SERVER but does not refresh
  // the local currentUser snapshot. Nobody is normally signed in on this
  // screen, so this is usually a no-op — but if a stale session does exist
  // (e.g. registerCompany left one behind), skipping the reload would leave
  // auth.currentUser.emailVerified stuck at false for the rest of the page's
  // life. Non-fatal: the activation itself already succeeded above.
  try {
    await auth.currentUser?.reload();
  } catch {
    /* ignore — the server-side state is what sign-in actually re-reads */
  }
};

/**
 * Asks the backend to generate and email a FRESH activation link to an
 * approved company.
 *
 * Needed because Firebase action codes are single-use and time-limited, so
 * the original link in the approval email is one bad outcome away from being
 * useless — it expires, a mail scanner prefetches it, the user clicks twice,
 * or link generation failed at approval time and the email shipped without a
 * working button at all. Without this, the only recovery was a coordinator
 * manually flipping the account in the Firebase console.
 *
 * Deliberately callable while signed OUT: a company that cannot activate
 * also cannot sign in, so requiring auth here would be a deadlock. The
 * server never confirms whether the address matched an account — see the
 * resendCompanyActivation Cloud Function.
 *
 * @param {string} email
 * @returns {Promise<{ sent: boolean }>} always { sent: true } on success,
 *   regardless of whether an account actually existed
 */
export const resendCompanyActivation = async (email) => {
  const call = httpsCallable(functions, "resendCompanyActivation");
  try {
    const { data } = await call({ email: email.trim().toLowerCase() });
    return data;
  } catch (err) {
    // When a callable fails before it can return a real HttpsError — the
    // request never reaches the function (CORS, missing deploy, Cloud Run
    // refusing the invoker) or the handler throws something that isn't an
    // HttpsError — there is no message to send back, so the SDK falls back to
    // using the status code AS the message. The user then sees a bare
    // "internal", which reads like a bug in the page rather than a backend
    // failure and tells whoever is debugging nothing at all. Detect that case
    // and say something that at least identifies the failure.
    const code = (err.code || "").replace(/^functions\//, "");
    const raw  = (err.message || "").trim().toLowerCase();
    if (!raw || raw === code) {
      throw new Error(`Couldn't reach the activation service (${code || "unknown error"}). Please try again — if this keeps happening, contact support.`);
    }
    throw new Error(err.message);
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// PASSWORD RESET — In-App (No Email Link Required)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Resets password in-app without using Firebase email links.
 * User must provide their current password to reauthenticate before changing it.
 * 
 * Flow:
 * 1. User enters email in ForgotPasswordScreen
 * 2. User enters current + new password in ResetPasswordScreen
 * 3. This function reauthenticates with current password (proves they own it)
 * 4. Updates password to the new one
 * 5. Success screen shows "Continue" button opening sign-in in new tab
 *
 * @param {string} email — user's email
 * @param {string} currentPassword — user's current password (to verify identity)
 * @param {string} newPassword — the new password to set
 * @returns {Promise<void>}
 * @throws descriptive Error (safe to show in UI)
 * 
 * @example
 * try {
 *   await resetPasswordInApp("user@example.com", "OldPassword123!", "NewPassword456!");
 *   // Success — now user can sign in with NewPassword456!
 * } catch (err) {
 *   console.error(err.message); // "Current password is incorrect."
 * }
 */
export const resetPasswordInApp = async (email, currentPassword, newPassword) => {
  const normalizedEmail = email.trim().toLowerCase();

  try {
    // 1. Reauthenticate to verify they own this account
    const cred = await signInWithEmailAndPassword(auth, normalizedEmail, currentPassword);
    const user = cred.user;

    if (!user) throw new Error("User not found after authentication.");

    // 2. Update password
    await updatePassword(user, newPassword);

    // 3. Quietly sign out — this forces them to log in with the new password
    // (otherwise they stay logged in with old session)
    // Optional: you could skip this if you want to keep them logged in
    // For security, it's better to force re-login with new credentials
    // await signOut(auth);

  } catch (err) {
    // Handle specific Firebase errors with user-friendly messages
    if (err.code === "auth/wrong-password" || err.code === "auth/invalid-credential") {
      throw new Error("Current password is incorrect.");
    }
    if (err.code === "auth/user-not-found") {
      throw new Error("User not found. Please check your email.");
    }
    if (err.code === "auth/weak-password") {
      throw new Error("New password is too weak.");
    }
    if (err.code === "auth/requires-recent-login") {
      throw new Error("Please sign in again before changing your password.");
    }
    // Fallback for unexpected errors
    throw new Error(err.message || "Failed to reset password. Please try again.");
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// PASSWORD RESET — Firebase Email Link (Older approach — kept for reference)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Sends a password-reset email only if the email exists in Firestore
 * (checks coordinators, students, and companies collections).
 * 
 * NOTE: This uses Firebase email links (oobCode), which can expire or fail.
 * Consider using resetPasswordInApp() instead for a more reliable flow.
 *
 * @param {string} email
 * @throws descriptive Error safe to show in the UI
 */
export const resetPassword = async (email) => {
  const normalized = email.trim().toLowerCase();

  // Check all three collections for a matching email. Companies and
  // coordinators additionally check `pendingEmail` — an account that already
  // clicked the confirmation link for an email change (see
  // requestUserEmailChange above) has a fully verified, real Auth account
  // under this new address, but Firestore hasn't reconciled `email` to match
  // yet (that only happens on their next successful signIn()). Without this,
  // "Forgot Password" would wrongly report the new email as unregistered
  // until they log in at least once — exactly backwards for someone using
  // this screen because they can't log in. sendPasswordResetEmail() itself
  // still safely rejects a pendingEmail that hasn't actually been verified
  // yet (Firebase doesn't recognize it as an Auth account email until the
  // link is clicked), so this doesn't loosen anything security-wise — it
  // just stops Firestore lag from blocking a legitimate reset.
  const cols = ["coordinators", "students", "companies"];
  let found = false;
  for (const col of cols) {
    const snap = await getDocs(query(collection(db, col), where("email", "==", normalized)));
    if (!snap.empty) { found = true; break; }
    if (col === "companies" || col === "coordinators") {
      const pendingSnap = await getDocs(query(collection(db, col), where("pendingEmail", "==", normalized)));
      if (!pendingSnap.empty) { found = true; break; }
    }
  }

  if (!found) {
    // Don't leak whether the email exists or not. Return quietly instead of
    // throwing: the caller shows the same generic "check your email" result
    // either way, and student personal emails (which never match the
    // Firestore `email` field above) are handled by requestStudentPasswordReset.
    return;
  }

  try {
    await sendPasswordResetEmail(auth, normalized, {
      // Firebase's own hosted page handles the reset itself (mode=resetPassword).
      // This "url" is just the "Continue" destination shown after that's done —
      // send the user straight to sign-in, no custom in-app reset screen involved.
      url: "https://ojtern.web.app/signin",
    });
  } catch (err) {
    if (err.code === "auth/too-many-requests") {
      throw new Error("Too many attempts. Please wait a moment before trying again.");
    }
    if (err.code === "auth/user-not-found") {
      // Reachable specifically via the pendingEmail check above: the address
      // is on file as a pending change, but its confirmation link hasn't
      // been clicked yet, so Firebase doesn't recognize it as an Auth
      // account email — nothing to send a reset link to.
      throw new Error("This email is on file as a pending change. Please confirm the verification link sent to it first, then try again.");
    }
    throw new Error("Failed to send reset email. Please try again later.");
  }
};

/**
 * Student password reset by PERSONAL email.
 *
 * Students sign in with a Student ID and (for bulk-created accounts) a
 * system-generated login address nobody can receive mail at, so
 * resetPassword() above can never find them. This calls the
 * requestStudentPasswordReset Cloud Function (functions/index.js), which looks
 * the student up through the studentPersonalEmails/{email} index and mails the
 * reset link to that personal email.
 *
 * The function always answers { sent: true }, whether or not the address
 * belongs to a student, so this can't be used to discover who is registered.
 *
 * @param {string} email — the student's personal email
 * @returns {Promise<{ sent: boolean }>}
 */
export const requestStudentPasswordReset = async (email) => {
  const call = httpsCallable(functions, "requestStudentPasswordReset");
  try {
    const { data } = await call({ email: email.trim().toLowerCase() });
    return data;
  } catch (err) {
    // Bare "internal" / not-found = the request never reached the function
    // (not deployed, wrong region, blocked network). Say so instead of "internal".
    const code = (err.code || "").replace(/^functions\//, "");
    const raw  = (err.message || "").trim().toLowerCase();
    if (!raw || raw === code || raw === "internal" || code === "not-found") {
      throw new Error(`Couldn't reach the reset service (${code || "unknown error"}). Please try again — if this keeps happening, contact support.`);
    }
    throw new Error(err.message);
  }
};

/**
 * Verifies a Firebase password reset code (from email link).
 * Called before allowing the user to enter a new password.
 *
 * @param {string} oobCode — Firebase's reset code from the email link
 * @returns {Promise<string>} the email address associated with this reset code
 * @throws Error if code is invalid or expired
 */
export const verifyResetCode = async (oobCode) => {
  try {
    return await verifyPasswordResetCode(auth, oobCode);
  } catch (err) {
    if (err.code === "auth/expired-oob-code") {
      throw new Error("This password reset link has expired. Please request a new one.");
    }
    if (err.code === "auth/invalid-oob-code") {
      throw new Error("This password reset link is invalid. Please request a new one.");
    }
    throw err;
  }
};

/**
 * Confirms a Firebase password reset (using the oobCode from email link).
 * This is the final step in the email-link password reset flow.
 *
 * @param {string} oobCode — Firebase's reset code from the email link
 * @param {string} newPassword — the new password to set
 * @throws Error if code is expired or password is weak
 */
export const confirmReset = async (oobCode, newPassword) => {
  try {
    await confirmPasswordReset(auth, oobCode, newPassword);
  } catch (err) {
    if (err.code === "auth/expired-oob-code") {
      throw new Error("This password reset link has expired. Please request a new one.");
    }
    if (err.code === "auth/invalid-oob-code") {
      throw new Error("This password reset link is invalid. Please request a new one.");
    }
    if (err.code === "auth/weak-password") {
      throw new Error("Password is too weak. Please use a stronger password.");
    }
    throw err;
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// COMPANY — approve/reject
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Approves a company for ONE of its registered Departments.
 *
 * IMPORTANT: a company can register under more than one Department/Program
 * (see `deptSelections` on the company doc). Approval is per-entry — a
 * coordinator can only approve the Department(s) matching their own
 * assignment, and the company only gains posting/student access for that
 * specific Department until each other Department's coordinator approves
 * it separately too. (Previously this set one company-wide `status` field,
 * which meant ANY coordinator approving silently unlocked ALL of a
 * multi-department company's departments — see the note in the write-up.)
 *
 * The top-level `status` field (used for sign-in gating, see signIn() above)
 * flips to "approved" the moment the FIRST department approves them — so the
 * company can log in and use the dashboard — but that alone does not grant
 * posting access to departments that haven't approved them yet. Whatever
 * screen creates job postings / pulls students MUST check the specific
 * `deptSelections` entry's own `status`, not the top-level one.
 *
 * @param {string} companyId
 * @param {string} coordinatorUid
 * @param {string} department  — must match one of the company's deptSelections[].department values
 */
export const approveCompanyDepartment = (companyId, coordinatorUid, department, program) =>
  decideCompanyAffiliation(companyId, coordinatorUid, department, program, "approved");

/**
 * Rejects a company for ONE of its registered Department/Program entries.
 * Other entries are unaffected — see approveCompanyDepartment above for why
 * this is per-entry, not per-company.
 */
export const rejectCompanyDepartment = (companyId, coordinatorUid, department, program) =>
  decideCompanyAffiliation(companyId, coordinatorUid, department, program, "rejected");

/**
 * Shared approve/reject for one affiliation entry (OJTern workflow rules):
 *
 *  • Matches the exact Department AND Program the coordinator acted on
 *    (normalised, so dash/spacing/case drift doesn't matter). Previously it
 *    matched Department only, so approving CCS — BSIT also approved every
 *    other CCS program the company had, including WITHDRAWN ones.
 *  • Only PENDING entries can be decided. Withdrawn, already-approved and
 *    already-rejected entries are never touched.
 *  • `program` is optional for older callers: when it's undefined, every
 *    PENDING entry under that Department is decided (the old behaviour, minus
 *    the withdrawn/approved bug).
 *  • Adds a history entry to the affiliation and notifies the company in the
 *    same transaction, so the notice exists only if the decision was saved.
 *
 * Throws if nothing was pending any more (e.g. the company withdrew the
 * program while the coordinator had the page open).
 */
const decideCompanyAffiliation = (companyId, coordinatorUid, department, program, decision) =>
  runTransaction(db, async (transaction) => {
    const companyRef = doc(db, "companies", companyId);
    const snap = await transaction.get(companyRef);
    if (!snap.exists()) throw new Error("Company not found.");
    const data = snap.data();
    const now = Timestamp.now();

    const matchesTarget = (entry) =>
      normalizeScope(entry.department) === normalizeScope(department) &&
      (program === undefined || normalizeScope(entry.program) === normalizeScope(program));

    const decided = [];
    const deptSelections = (data.deptSelections || []).map(entry => {
      if (!matchesTarget(entry) || (entry.status || "pending") !== "pending") return entry;
      decided.push(entry);
      const history = Array.isArray(entry.history) ? entry.history : [];
      return {
        ...entry,
        status: decision,
        decidedAt: now,
        ...(decision === "approved"
          ? { approvedBy: coordinatorUid, approvedAt: now }
          : { rejectedBy: coordinatorUid, rejectedAt: now }),
        history: [...history, { status: decision, at: now, by: coordinatorUid || "" }],
      };
    });

    if (decided.length === 0) {
      throw new Error("This request is no longer pending — the company may have withdrawn or changed it.");
    }

    const anyApproved = deptSelections.some(e => e.status === "approved");
    // Withdrawn entries don't count either way when deciding the account status.
    const active = deptSelections.filter(e => e.status !== "withdrawn");
    const allRejected = active.length > 0 && active.every(e => e.status === "rejected");

    transaction.update(companyRef, {
      deptSelections,
      // Account-level status only ever moves FORWARD out of "pending" here —
      // "approved"/"suspended"/"blocked" set elsewhere are never clobbered
      // (see applyCompanyEnforcement below).
      ...(decision === "approved" && data.status === "pending" && anyApproved ? { status: "approved" } : {}),
      ...(decision === "rejected" && data.status === "pending" && allRejected && !anyApproved ? { status: "rejected" } : {}),
    });

    const labels = decided.map(e => [e.department, e.program].filter(Boolean).join(" — ")).join(", ");
    transaction.set(doc(collection(db, "notifications")), {
      recipientId:   companyId,
      recipientRole: "company",
      type:          decision === "approved" ? "affiliation_approved" : "affiliation_rejected",
      title:         decision === "approved" ? "Course approved" : "Course declined",
      message:       decision === "approved"
        ? `The coordinator approved ${labels}. You can now select ${decided.length > 1 ? "them" : "it"} when creating or editing OJT posts.`
        : `The coordinator declined ${labels}. You can contact the coordinator for details.`,
      coordinatorUid: coordinatorUid || null,
      read:          false,
      createdAt:     serverTimestamp(),
    });

    return { decided: decided.map(e => ({ department: e.department, program: e.program || "" })) };
  });

// ─────────────────────────────────────────────────────────────────────────────
// ACTIVITY LOG
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Records a coordinator action to the shared activity log, shown as a
 * "Recent Activity" feed on the coordinator dashboard.
 * @param {string} coordinatorUid
 * @param {string} action     short machine-readable action key, e.g. "company_approved"
 * @param {string} description human-readable summary, e.g. "Approved Gueco Repair Shops"
 * @param {object} [meta]     optional extra fields (e.g. targetId, targetName)
 */
export const logActivity = (coordinatorUid, action, description, meta = {}) =>
  addDoc(collection(db, "activity_logs"), {
    coordinatorUid,
    action,
    description,
    ...meta,
    createdAt: serverTimestamp(),
  });

// ─────────────────────────────────────────────────────────────────────────────
// COMPANY DISCIPLINARY ACTIONS — enforcement, audit trail, company notice
// ─────────────────────────────────────────────────────────────────────────────
// Lives here (not in a screen component) because it's account/auth logic:
// it's what makes accountStatus actually mean something at sign-in time
// (see signIn() above), not just a label shown in a UI.
//
// A company's "good standing" status is "approved" (see registerCompany /
// approveCompany above) — NOT "active" ("active" is used for students and
// coordinators). Suspend/Block temporarily or permanently override that
// same `status` field on the company doc; reactivation restores "approved".

// A company auto-escalates to "suspended" once it racks up this many
// RESOLVED disciplinary actions while still in good standing. Tune this
// number to whatever the panel/adviser agrees on.
const AUTO_SUSPEND_THRESHOLD = 3;

// Coordinator picks the duration when they suspend someone directly. When the
// system auto-escalates on its own (no coordinator input at that moment),
// this is the default length used instead.
const MAX_SUSPENSION_DAYS = 31;   // longer than a month → Block instead
const AUTO_SUSPEND_DEFAULT_DAYS = 7;

/**
 * Applies the real effect of a coordinator action on the company's own
 * document (not just a report). Runs as a transaction so concurrent actions
 * on the same company can't race the action counter.
 *
 * @param {string} companyId
 * @param {"Require Correction"|"Warning Issued"|"Suspend Account"|"Block Account"|string} actionType
 * @param {string} coordinatorUid
 * @param {number|string} [suspensionDays]
 * @returns {Promise<{ status: string, previousStatus: string, autoEscalated: boolean, suspendedUntil: Timestamp|null } | null>}
 */
export const applyCompanyEnforcement = async (companyId, actionType, coordinatorUid, suspensionDays) => {
  if (!companyId) {
    console.warn("applyCompanyEnforcement: no companyId given — skipping.");
    return null;
  }
  const companyRef = doc(db, "companies", companyId);

  return runTransaction(db, async (transaction) => {
    const companySnap = await transaction.get(companyRef);
    if (!companySnap.exists()) {
      console.warn(`Company ${companyId} not found — skipping enforcement.`);
      return null;
    }

    const data = companySnap.data();
    const previousStatus = data.status || "approved";
    const actionCount = (data.disciplinaryActionCount || 0) + 1;

    let nextStatus = previousStatus;
    let statusReason = null;
    let autoEscalated = false;
    let suspendedUntil = null; // Timestamp | null — null means not a (timed) suspension

    if (actionType === "Block Account") {
      nextStatus = "blocked";
      statusReason = "Blocked following coordinator review.";
      // Blocks have no expiry — a human has to lift this one.
    } else if (actionType === "Suspend Account") {
      nextStatus = "suspended";
      statusReason = "Suspended following coordinator review.";
      // Held to 1–31 days here as well as in the Resolve window: a suspension
      // is a temporary hold, and an absurd value (a pasted 10000000000) would
      // otherwise become a permanent lockout nobody can undo.
      const requested = Math.floor(Number(suspensionDays));
      const days = Number.isFinite(requested) && requested > 0
        ? Math.min(requested, MAX_SUSPENSION_DAYS)
        : AUTO_SUSPEND_DEFAULT_DAYS;
      suspendedUntil = Timestamp.fromMillis(Date.now() + days * 24 * 60 * 60 * 1000);
    }
    // "Require Correction", "Warning Issued", and "Others" intentionally do
    // NOT change nextStatus — the account stays active/approved per spec.

    // Auto-escalate only if the coordinator didn't already suspend/block
    // outright, and the company is still in good standing but has piled up
    // disciplinary actions.
    if (nextStatus === previousStatus && previousStatus !== "suspended" && previousStatus !== "blocked" && actionCount >= AUTO_SUSPEND_THRESHOLD) {
      nextStatus = "suspended";
      statusReason = `Auto-suspended after reaching ${actionCount} disciplinary actions.`;
      autoEscalated = true;
      suspendedUntil = Timestamp.fromMillis(Date.now() + AUTO_SUSPEND_DEFAULT_DAYS * 24 * 60 * 60 * 1000);
    }

    const update = { disciplinaryActionCount: actionCount };
    if (nextStatus !== previousStatus) {
      update.status           = nextStatus;
      update.statusReason     = statusReason;
      update.statusUpdatedAt  = serverTimestamp();
      update.statusUpdatedBy  = coordinatorUid || "";
      // suspendedUntil is only meaningful for "suspended"; clear it for
      // "blocked" (indefinite) or anything that isn't a timed suspension.
      update.suspendedUntil   = nextStatus === "suspended" ? suspendedUntil : null;
    }

    transaction.update(companyRef, update);
    return { status: nextStatus, previousStatus, autoEscalated, suspendedUntil };
  });
};

/**
 * Auto-reactivation: since suspensions can't "expire" on their own at the
 * exact minute without a scheduled backend job, something has to check the
 * expiry date and flip the status back. Called from signIn() right before a
 * company account status is evaluated, so an expired suspension never
 * wrongly blocks a login. Safe/cheap no-op if not suspended or not expired.
 *
 * @param {string} companyId
 */
export const checkAndReactivateCompany = async (companyId) => {
  if (!companyId) return null;
  const companyRef = doc(db, "companies", companyId);

  return runTransaction(db, async (transaction) => {
    const companySnap = await transaction.get(companyRef);
    if (!companySnap.exists()) return null;

    const data = companySnap.data();
    if (data.status !== "suspended" || !data.suspendedUntil) return null;

    const expiry = data.suspendedUntil.toMillis
      ? data.suspendedUntil.toMillis()
      : new Date(data.suspendedUntil).getTime();

    if (Date.now() < expiry) return null; // not expired yet

    transaction.update(companyRef, {
      status:          "approved", // companies' good-standing status, NOT "active"
      statusReason:    "Suspension period ended — auto-reactivated.",
      statusUpdatedAt: serverTimestamp(),
      statusUpdatedBy: "system",
      suspendedUntil:  null,
    });
    return { reactivated: true };
  });
};

/**
 * Records a coordinator disciplinary action to a dedicated audit-trail
 * collection, separate from the general "Recent Activity" feed (activity_logs)
 * so it can be queried per-company and carries the exact fields required for
 * compliance review.
 *
 * @param {object} entry
 * @param {string} entry.companyId
 * @param {string} entry.companyName
 * @param {string} entry.coordinatorId
 * @param {string} entry.coordinatorName
 * @param {string} entry.actionType
 * @param {string} entry.reason            — the resolution/reason text
 * @param {string} entry.previousAccountStatus
 * @param {string} entry.newAccountStatus
 */
export const recordCompanyAction = (entry) =>
  addDoc(collection(db, "companyActions"), {
    companyId:             entry.companyId,
    // Links the entry to the report it resolved, so getCompanyActionHistory can
    // tell it apart from the same action rebuilt from that report.
    reportId:              entry.reportId || null,
    companyName:           entry.companyName || "",
    coordinatorId:         entry.coordinatorId,
    coordinatorName:       entry.coordinatorName || "Coordinator",
    actionType:            entry.actionType,
    reason:                entry.reason || "",
    previousAccountStatus: entry.previousAccountStatus || "approved",
    newAccountStatus:      entry.newAccountStatus || entry.previousAccountStatus || "approved",
    createdAt:             serverTimestamp(),
  });

/**
 * Fetches the disciplinary action history for a single company, most recent
 * first — used by the Coordinator's "View Action History" panel.
 * @param {string} companyId
 * @returns {Promise<object[]>}
 */
export const getCompanyActionHistory = async (companyId) => {
  if (!companyId) return [];

  // Sorted here instead of with orderBy("createdAt"): where + orderBy on two
  // different fields needs a composite index, and without one the query
  // fails outright — which showed up as an empty history.
  const toMillis = (t) => (t && typeof t.toMillis === "function" ? t.toMillis() : 0);

  // Each source is read independently: if one is unavailable (for example
  // companyActions before its Firestore rule is published), the other still
  // shows. Only when BOTH fail is it reported as an error.
  const [actionsResult, reportsResult] = await Promise.allSettled([
    getDocs(query(collection(db, "companyActions"), where("companyId", "==", companyId))),
    // Resolved reports carry the action, notes, coordinator and date too.
    // Actions taken while companyActions writes were being blocked by the
    // Firestore rules only exist here, so they're rebuilt from the report.
    getDocs(query(collection(db, "reports"), where("companyId", "==", companyId))),
  ]);

  if (actionsResult.status === "rejected" && reportsResult.status === "rejected") {
    throw reportsResult.reason;
  }
  if (actionsResult.status === "rejected") {
    console.warn("[ActionHistory] companyActions unavailable — showing actions from resolved reports only:", actionsResult.reason);
  }
  if (reportsResult.status === "rejected") {
    console.warn("[ActionHistory] reports unavailable — showing recorded actions only:", reportsResult.reason);
  }

  const actionsSnap = actionsResult.status === "fulfilled" ? actionsResult.value : { docs: [], size: 0 };
  const reportsSnap = reportsResult.status === "fulfilled" ? reportsResult.value : { docs: [], size: 0 };

  const recorded = actionsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
  const recordedReportIds = new Set(recorded.map((a) => a.reportId).filter(Boolean));

  const fromReports = reportsSnap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((r) => r.status === "resolved" && r.resolutionAction && !recordedReportIds.has(r.id))
    .map((r) => ({
      id: `report-${r.id}`,
      reportId: r.id,
      companyId,
      companyName: r.company || "",
      coordinatorId: r.resolvedBy || "",
      coordinatorName: r.resolvedByName || "Coordinator",
      actionType: r.resolutionAction,
      reason: r.resolutionNotes || "",
      // Not stored on the report, so not shown for rebuilt entries.
      previousAccountStatus: null,
      newAccountStatus: null,
      createdAt: r.resolvedAt || null,
      fromReport: true,
    }));

  // One line that explains an empty history: shows whether the company's
  // reports were found at all and how many count as actions.
  console.debug(
    `[ActionHistory] companyId=${companyId} | companyActions=${recorded.length}` +
    ` | reports=${reportsSnap.size} (resolved with an action: ${fromReports.length + recordedReportIds.size})`
  );

  return [...recorded, ...fromReports].sort((x, y) => toMillis(y.createdAt) - toMillis(x.createdAt));
};

/**
 * Notifies a company about a coordinator action by dropping a message into
 * their existing conversation with the coordinator — reuses the same
 * conversations/{convId}/messages schema as useChat.js (see makeConvId,
 * sendMessage there) instead of a separate notification system, so it shows
 * up naturally in both the Coordinator's and the Company's Messages screens,
 * with the same unread-badge behavior.
 *
 * @param {string} coordinatorUid
 * @param {string} coordinatorName
 * @param {string} companyId
 * @param {string} companyName
 * @param {string} text
 */
/**
 * Notification for a COMPANY — the counterpart of the student notifications
 * StudentDashboardScreen reads. Coordinator decisions (warnings, corrections,
 * suspensions, blocks) belong in the company's notification bell, not in the
 * chat thread: chat is for conversation, and a decision pushed there reads as
 * if the coordinator had typed it personally.
 *
 * `recipientId` is the field to query on. Student notifications use
 * `studentId`, which stays as it is so nothing existing breaks.
 */
/**
 * Notification for whoever FILED a report — the student (or company) waiting to
 * hear what came of it. Nothing used to be sent back to them: evidence went in
 * and nothing came out, which reads as "nobody looked".
 *
 * Student notifications are read by `studentId` (StudentDashboardScreen) and
 * company ones by `recipientId` (CompanyDashboardScreen), so both fields are
 * written and each dashboard picks up the one it queries.
 */
export const notifyReporter = async (reporterId, reporterRole, { title, body, reportId = null }) => {
  if (!reporterId || !body) return;
  const isStudent = String(reporterRole || "").toLowerCase() === "student";
  await addDoc(collection(db, "notifications"), {
    // Students read notifications by `studentId` and the dashboard HIDES any
    // notice that also has a `recipientId` (it treats those as company or
    // coordinator notices) -- so a student reporter gets studentId only, and a
    // company reporter gets recipientId only.
    ...(isStudent ? { studentId: reporterId } : { recipientId: reporterId }),
    recipientRole: isStudent ? "student" : "company",
    title:         title || "Report update",
    message:       body,
    type:          "report_outcome",
    reportId,
    read:          false,
    createdAt:     serverTimestamp(),
  });
};

/**
 * A company registers under one or more Department/Program pairs (deptSelections)
 * and each Department's coordinator approves it separately. When ONE
 * coordinator blocks the company, the coordinators of its OTHER departments
 * would otherwise only find out when an application or a post stops working.
 * This drops a notice for each of those Department/Program scopes.
 *
 * - Only entries that still matter are notified: "approved" (the company is
 *   active there) and "pending" (a request that's now moot). Rejected and
 *   withdrawn entries are skipped.
 * - The blocking coordinator's own department(s) are skipped -- they just did it.
 * - Shaped like the other coordinator-addressed notices (see the post_closed
 *   notice in CompanyCreatePostScreen): recipientRole "coordinator", the
 *   department + program it applies to, and readBy[].
 *
 * @returns {Promise<number>} how many Department/Program scopes were notified
 */
export const notifyOtherCoordinatorsCompanyBlocked = async (companyId, companyName, actingCoordinatorUid) => {
  if (!companyId) return 0;
  const [companySnap, actorSnap] = await Promise.all([
    getDoc(doc(db, "companies", companyId)),
    actingCoordinatorUid ? getDoc(doc(db, "coordinators", actingCoordinatorUid)) : Promise.resolve(null),
  ]);
  if (!companySnap.exists()) return 0;

  const name = companyName || companySnap.data().companyName || "A company";
  const actorDepartments = new Set(
    ((actorSnap && actorSnap.exists() ? actorSnap.data().deptSelections : null) || [])
      .map(e => normalizeScope(e.department))
      .filter(Boolean)
  );

  const targets = new Map();   // one notice per Department + Program
  (companySnap.data().deptSelections || []).forEach(e => {
    const status = e.status || "pending";
    if (status !== "approved" && status !== "pending") return;
    if (!e.department || actorDepartments.has(normalizeScope(e.department))) return;
    targets.set(
      `${normalizeScope(e.department)}||${normalizeScope(e.program)}`,
      { department: e.department, program: e.program || "" }
    );
  });

  await Promise.all([...targets.values()].map(t =>
    addDoc(collection(db, "notifications"), {
      recipientRole: "coordinator",
      department:    t.department,
      program:       t.program,
      type:          "company_blocked",
      companyId,
      companyName:   name,
      message:       `${name} was blocked by another coordinator after a report review. The company can no longer sign in or post, which may affect students of yours who are applying or placed there.`,
      readBy:        [],
      createdAt:     serverTimestamp(),
    })
  ));
  return targets.size;
};

/**
 * Notification for the STUDENT a report is about -- the counterpart of
 * notifyCompanyAccount for company reports. Without it a coordinator's decision
 * on a report about a student only reached whoever filed it; the student never
 * heard anything.
 *
 * Shaped for StudentDashboardScreen: read by `studentId == uid`, NO recipientId
 * (the dashboard hides notices that have one), and `action` + `reportId` so
 * Warning Issued / Require Correction / Others open the response form.
 */
export const notifyReportedStudent = async (studentUid, { title, body, action = null, reportId = null, coordinatorUid = null }) => {
  if (!studentUid || !body) return;
  await addDoc(collection(db, "notifications"), {
    studentId:     studentUid,
    recipientRole: "student",
    action,
    title:         title || "Report update",
    message:       body,
    type:          "report_resolution",
    reportId,
    coordinatorUid,
    read:          false,
    createdAt:     serverTimestamp(),
  });
};

export const notifyCompanyAccount = async (companyId, { title, body, type = "account", reportId = null, coordinatorUid = null, action = null }) => {
  if (!companyId || !body) return;
  await addDoc(collection(db, "notifications"), {
    recipientId:   companyId,
    recipientRole: "company",
    // The coordinator's action ("Warning Issued", "Require Correction", …).
    // ReportResponseModal reads it to decide whether proof is required.
    action,
    title:         title || "Account update",
    message:       body,
    type,
    reportId,
    coordinatorUid,
    read:          false,
    createdAt:     serverTimestamp(),
  });
};

export const notifyCompany = async (coordinatorUid, coordinatorName, companyId, companyName, text) => {
  if (!coordinatorUid || !companyId || !text) return;
  const convId  = [coordinatorUid, companyId].sort().join("_"); // matches useChat.js makeConvId
  const convRef = doc(db, "conversations", convId);
  const snap    = await getDoc(convRef);

  if (!snap.exists()) {
    await setDoc(convRef, {
      participants:     [coordinatorUid, companyId],
      participantNames: { [coordinatorUid]: coordinatorName || "Coordinator", [companyId]: companyName || "Company" },
      participantRoles: { [coordinatorUid]: "coordinator", [companyId]: "company" },
      lastMessage:      null,
      updatedAt:        serverTimestamp(),
      createdAt:        serverTimestamp(),
    });
  }

  await addDoc(collection(db, "conversations", convId, "messages"), {
    text,
    senderId:    coordinatorUid,
    ts:          serverTimestamp(),
    edited:      false,
    unsent:      false,
    attachments: null,
  });

  await updateDoc(convRef, {
    lastMessage: { text, senderId: coordinatorUid, ts: serverTimestamp() },
    updatedAt:   serverTimestamp(),
    deletedFor:  [],
  });
};

// ─────────────────────────────────────────────────────────────────────────────
// USER PROFILE FETCH (generic helper)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Fetches a user's Firestore profile.
 * @param {"coordinators"|"students"|"companies"} collectionName
 * @param {string} uid
 * @returns {Promise<object|null>}
 */
export const getUserProfile = async (collectionName, uid) => {
  const snap = await getDoc(doc(db, collectionName, uid));
  return snap.exists() ? snap.data() : null;
};

// ─────────────────────────────────────────────────────────────────────────────
// STUDENT — Create account (coordinator only)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Generates the default student password.
 * Format: FirstInitial(upper) + lastName + last3DigitsOfStudentId + "." + college
 * e.g. firstName "Juan", lastName "Dela Cruz", studentId "2021-00123", college "CCS"  →  "Jdelacruz123.ccs"
 *
 * The leading initial is capitalized (rather than lowercased) so the
 * password always contains an uppercase character — this Firebase project's
 * password policy requires one, and an all-lowercase password like
 * "jdelacruz123.ccs" gets rejected with auth/password-does-not-meet-
 * requirements at account-creation time.
 *
 * @param {string} firstName
 * @param {string} lastName
 * @param {string} studentId
 * @param {string} collegeAbbr  — SHORT code, e.g. "CCS", "CBA" (NOT the full
 *   College name — callers must pass the abbreviation, e.g. via
 *   departments[college]?.abbr from useDepartmentsPrograms, so the password
 *   stays short/typeable; see createStudentAccount below)
 * @returns {string}
 */
export const generateStudentPassword = (firstName, lastName, studentId, collegeAbbr) => {
  const firstInitial = firstName.trim()[0].toUpperCase();
  const cleanLast    = lastName.trim().toLowerCase().replace(/\s+/g, "");
  const last3        = String(studentId).trim().replace(/\D/g, "").slice(-3);
  const cleanCollege = collegeAbbr.trim().toLowerCase().replace(/\s+/g, "");
  return `${firstInitial}${cleanLast}${last3}.${cleanCollege}`;
};

/**
 * Creates a student account (coordinator only).
 * Generates a default password from lastName + college, creates Auth + Firestore profile,
 * then signs out to avoid logging the coordinator out.
 *
 * @param {object} studentData — {
 *   studentId, lastName, middleInitial, firstName,
 *   college, program, specialization, yearSection,
 *   sex, age, email,
 *   collegeAbbr — OPTIONAL short code (e.g. "CCS") used only for the
 *     generated password suffix. `college` itself is always the full name
 *     ("College of Computer Studies") — the same canonical form used by
 *     companies/coordinators/posts everywhere else in the app — and is what
 *     actually gets saved to Firestore. If collegeAbbr isn't supplied this
 *     falls back to `college` itself, which makes for a long password but
 *     never breaks account creation.
 * }
 * @param {string} createdByUid — coordinator's UID
 * @returns {Promise<{ uid: string, password: string }>}
 *   Returns the UID and generated password so coordinator can share it with the student
 */
export const createStudentAccount = async (studentData, createdByUid) => {
  const {
    studentId, lastName, middleInitial, firstName, middleName,
    college, program, specialization, yearSection,
    sex, age, email, collegeAbbr, batch,
  } = studentData;

  // 1. Check for duplicate studentId
  const q = query(
    collection(db, "students"),
    where("studentId", "==", studentId.trim())
  );
  const snap = await getDocs(q);
  if (!snap.empty) {
    throw new Error(`Student ID "${studentId}" is already registered.`);
  }

  // 2. Generate default password — uses the short abbreviation when the
  // caller provided one, so the password suffix stays like ".ccs" rather
  // than the unwieldy ".collegeofcomputerstudies".
  const password = generateStudentPassword(firstName, lastName, studentId, collegeAbbr || college);

  // 2b. Bulk-imported students arrive here with email: "" — the import
  // template intentionally has no Email column, since students fill their
  // own email in later from their personal information page. But Firebase
  // Auth still requires SOME syntactically valid email to create the
  // account with — createUserWithEmailAndPassword throws auth/missing-email
  // on an empty string. That's what was breaking every imported row (e.g.
  // 202210112, 202210156), not just those two.
  //
  // Fall back to a deterministic placeholder derived from the student ID
  // when no real email was supplied. This becomes both the Auth credential
  // and the Firestore `email` field (which student sign-in resolves by
  // studentId — see signIn() above) until the student sets their real
  // email, same as manually-created students still go through the real
  // email path unaffected.
  const trimmedEmail = (email || "").trim();
  const hasRealEmail = trimmedEmail.length > 0;
  const authEmail = hasRealEmail
    ? trimmedEmail.toLowerCase()
    : `${studentId.trim()}@pending.student`;

  // 3. Firebase Auth — isolated so it doesn't sign the coordinator out
  const uid = await createAuthUserIsolated(authEmail, password, async (newUid) => {
    await setDoc(doc(db, "students", newUid), {
      uid:            newUid,
      studentId:      studentId.trim(),
      lastName:       lastName.trim(),
      middleInitial:  (middleInitial || "").trim(),
      // Full middle name when the bulk-import sheet provided one.
      ...(middleName && String(middleName).trim() ? { middleName: String(middleName).trim() } : {}),
      firstName:      firstName.trim(),
      fullName: `${firstName.trim()} ${(middleInitial || "").trim() ? (middleInitial || "").trim().replace(/\.$/, "") + ". " : ""}${lastName.trim()}`,
      college:        college.trim(),
      program:        (program || "").trim(),
      specialization: (specialization || "").trim(),
      yearSection:    yearSection.trim(),
      // Graduating batch year — distinct from yearSection ("4-A"). Set from the
      // New student form and the Batch column of the import template.
      batch:          String(batch || "").trim(),
      // Archive state, so every new account starts in the active list.
      isArchived:     false,
      archivedAt:     null,
      sex:            sex || "",
      // Bulk-imported students have no age yet (they enter it on first
      // login). Store null rather than Number("") === 0.
      age:            age === "" || age == null ? null : Number(age),
      email:          authEmail,
      // Lets the UI (and a future "add your email" first-login prompt)
      // distinguish a real address from the placeholder, without having to
      // pattern-match on "@pending.student".
      hasRealEmail,
      role:           "student",
      status:         "active",
      passwordChanged: false,
      createdBy:      createdByUid,
      createdAt:      serverTimestamp(),
    });
  });

  return { uid, password };
};

// ─────────────────────────────────────────────────────────────────────────────
// CHANGE PASSWORD — for first-login password change (students & coordinators)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Changes the current user's password and marks passwordChanged: true in Firestore,
 * then signs the user out so they land back on the sign-in screen.
 *
 * Reauthenticates first, since Firebase's updatePassword requires a "recent login".
 *
 * @param {string} currentPassword — the user's existing password
 * @param {string} newPassword
 * @param {"students"|"coordinators"} collectionName
 * @param {string} uid
 * @param {string} email
 * @throws Error with user-friendly message
 */
/**
 * Coordinator edits a student's account (CoordinatorStudentsAcccountScreen).
 * Runs server-side (functions/index.js → updateStudentAccount) because a change
 * to the name, Student ID or department also resets the student's DEFAULT
 * password, which only the Admin SDK can do. Age and personal email are the
 * student's own and are never changed here.
 *
 * @returns {Promise<{ ok: boolean, fullName: string, passwordReset: boolean, newPassword: string|null }>}
 */
export const updateStudentAccountByCoordinator = async ({ studentUid, updates, collegeAbbr }) => {
  try {
    const res = await httpsCallable(functions, "updateStudentAccount")({ studentUid, updates, collegeAbbr });
    return res.data;
  } catch (err) {
    // A bare "internal" (no message from our function) almost always means the
    // request never reached updateStudentAccount: it isn't deployed yet, it
    // was deployed to a different region than functions in ./firebase
    // (asia-southeast1), or the network blocked it. Errors raised by the
    // function itself always carry a readable message.
    const code = String(err?.code || "");
    const msg  = String(err?.message || "").trim();
    if (!msg || msg.toLowerCase() === "internal" || code === "functions/not-found") {
      throw new Error(
        "Couldn't reach the server to save this student. Make sure the updateStudentAccount Cloud Function " +
        "is deployed (firebase deploy --only functions:updateStudentAccount), then try again."
      );
    }
    if (code === "functions/unavailable" || code === "functions/deadline-exceeded") {
      throw new Error("No connection to the server. Check your internet connection, then try again.");
    }
    throw new Error(msg);
  }
};

export const changePassword = async (currentPassword, newPassword, collectionName, uid, email) => {
  const user = auth.currentUser;

  if (!user) {
    throw new Error("No user is currently signed in.");
  }

  const userEmail = email || user.email;
  if (!userEmail) {
    throw new Error("Unable to verify user email. Please try logging in again.");
  }

  // Reauthenticate with current password
  const credential = EmailAuthProvider.credential(userEmail, currentPassword);
  try {
    await reauthenticateWithCredential(user, credential);
  } catch (err) {
    console.error("[changePassword] reauthenticate failed:", {
      code: err.code, message: err.message,
    });
    if (err.code === "auth/wrong-password" || err.code === "auth/invalid-credential") {
      throw new Error("Your current password is incorrect.");
    }
    throw new Error("Your current password is incorrect.");
  }

  // Update password
  await updatePassword(user, newPassword);

  // Mark passwordChanged in Firestore
  try {
    await updateDoc(doc(db, collectionName, uid), {
      passwordChanged: true,
    });
  } catch (err) {
    console.error("[changePassword] updateDoc failed:", {
      code: err.code, message: err.message,
    });
    throw new Error(
      "Your password was changed, but we couldn't update your account status. Please contact support."
    );
  }

  await signOut(auth);
};

// ─────────────────────────────────────────────────────────────────────────────
// LOGIN-EMAIL CHANGE — companies & coordinators
// ─────────────────────────────────────────────────────────────────────────────
// Changing a user's *login* email is NOT the same as updating the `email`
// field on their Firestore doc — that field is just data. The actual
// credential Firebase Auth checks against at sign-in lives on the Auth
// account itself (`auth.currentUser.email`), and it's a separate system.
// Editing only the Firestore field (what the old code did, and what the
// coordinator profile screen was still doing) explains exactly the symptom
// reported: the OLD email kept working to log in (Auth account unchanged)
// and the NEW one didn't (Auth never heard about it).
//
// Firebase also won't let a signed-in user's email be swapped instantly on
// most projects anymore — "email enumeration protection" (on by default for
// projects created after Feb 2023) forces `updateEmail()` to fail with
// auth/operation-not-allowed, and requires `verifyBeforeUpdateEmail()`
// instead: a confirmation link is emailed to the NEW address, and the Auth
// account's email only actually changes once that link is clicked.
//
// So Firestore's `email` field is intentionally left untouched here — it
// still needs to work for login lookups in the meantime — and the new
// address is stashed in `pendingEmail` instead. See the reconciliation
// block in signIn() above for how `pendingEmail` gets promoted to `email`
// automatically the first time the user successfully logs in with it (proof
// the link was verified).
//
// @param {string} currentPassword — required to reauthenticate (Firebase
//   treats an email change as a sensitive operation; without a recent
//   sign-in it throws auth/requires-recent-login).
// @param {string} newEmail
// @param {string} uid
// @param {"companies"|"coordinators"} collectionName
// @returns {Promise<{ changed: boolean, pendingEmail?: string }>}
const requestUserEmailChange = async (currentPassword, newEmail, uid, collectionName) => {
  const user = auth.currentUser;
  if (!user) throw new Error("No user is currently signed in.");

  const normalizedNewEmail = newEmail.trim().toLowerCase();
  const currentAuthEmail   = (user.email || "").toLowerCase();

  if (normalizedNewEmail === currentAuthEmail) {
    return { changed: false }; // nothing to do — not actually a change
  }

  // Reauthenticate with the CURRENT (still-active) Auth email — not the new
  // one, which isn't a recognized credential yet.
  const credential = EmailAuthProvider.credential(user.email, currentPassword);
  try {
    await reauthenticateWithCredential(user, credential);
  } catch (err) {
    console.error(`[requestUserEmailChange:${collectionName}] reauthenticate failed:`, { code: err.code, message: err.message });
    if (err.code === "auth/wrong-password" || err.code === "auth/invalid-credential") {
      throw new Error("Your current password is incorrect.");
    }
    throw err;
  }

  try {
    // Preferred path on modern Firebase projects: sends a verification link,
    // doesn't change anything until it's clicked.
    await verifyBeforeUpdateEmail(user, normalizedNewEmail);
  } catch (err) {
    if (err.code === "auth/operation-not-allowed") {
      // This specific project has email enumeration protection disabled —
      // fall back to the older, immediate-change API instead.
      try {
        await updateEmail(user, normalizedNewEmail);
        await updateDoc(doc(db, collectionName, uid), {
          email:        normalizedNewEmail,
          pendingEmail: null,
        });
        return { changed: true, pendingEmail: null };
      } catch (fallbackErr) {
        console.error(`[requestUserEmailChange:${collectionName}] updateEmail fallback failed:`, { code: fallbackErr.code, message: fallbackErr.message });
        throw fallbackErr;
      }
    }
    if (err.code === "auth/email-already-in-use") {
      throw new Error("That email address is already in use.");
    }
    if (err.code === "auth/invalid-email") {
      throw new Error("Please enter a valid email address.");
    }
    console.error(`[requestUserEmailChange:${collectionName}] verifyBeforeUpdateEmail failed:`, { code: err.code, message: err.message });
    throw err;
  }

  // This bookkeeping write is intentionally non-fatal: verifyBeforeUpdateEmail
  // above already succeeded, meaning Firebase has already sent the real
  // verification email — that's what actually matters for the account to
  // work. If this Firestore write fails (e.g. security rules reject it),
  // we don't want to report the whole email change as failed when the
  // important part already went through. The self-healing reconciliation in
  // signIn() above will still sync Firestore's `email` field once the user
  // successfully logs in with the new address, even without a locally
  // tracked pendingEmail.
  try {
    await updateDoc(doc(db, collectionName, uid), {
      pendingEmail: normalizedNewEmail,
    });
  } catch (pendingErr) {
    console.error(`[requestUserEmailChange:${collectionName}] pendingEmail bookkeeping write failed (verification email was already sent):`, { code: pendingErr.code, message: pendingErr.message });
  }

  return { changed: true, pendingEmail: normalizedNewEmail };
};

/**
 * Changes a COMPANY's Firebase Auth login email (see requestUserEmailChange
 * above for the full mechanics and why this can't just write to Firestore).
 *
 * @param {string} currentPassword
 * @param {string} newEmail
 * @param {string} uid
 * @returns {Promise<{ changed: boolean, pendingEmail?: string }>}
 */
export const requestCompanyEmailChange = (currentPassword, newEmail, uid) =>
  requestUserEmailChange(currentPassword, newEmail, uid, "companies");

/**
 * Changes a COORDINATOR's Firebase Auth login email (see
 * requestUserEmailChange above for the full mechanics and why this can't
 * just write to Firestore).
 *
 * @param {string} currentPassword
 * @param {string} newEmail
 * @param {string} uid
 * @returns {Promise<{ changed: boolean, pendingEmail?: string }>}
 */
export const requestCoordinatorEmailChange = (currentPassword, newEmail, uid) =>
  requestUserEmailChange(currentPassword, newEmail, uid, "coordinators");

// ─────────────────────────────────────────────────────────────────────────────
// ISOLATED ACCOUNT CREATION — create a Firebase Auth user WITHOUT switching
// the current session to that new user (the normal client SDK behavior signs
// you in as whoever you just created, which we don't want here since a
// coordinator stays logged in as themselves while adding/creating accounts).
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Creates a Firebase Auth user without switching the current session.
 * If onCreated callback is provided and throws, the just-created Auth user is deleted.
 * This prevents orphaned Auth accounts with no Firestore doc.
 *
 * @param {string} email
 * @param {string} password
 * @param {function} [onCreated] — optional callback(uid) after Auth user is created
 * @returns {Promise<string>} the new user's UID
 */
const createAuthUserIsolated = async (email, password, onCreated) => {
  const tempApp = initializeApp(getApp().options, `temp-${Date.now()}`);
  const tempAuth = getAuth(tempApp);
  try {
    const { user } = await createUserWithEmailAndPassword(tempAuth, email, password);
    if (onCreated) {
      try {
        await onCreated(user.uid);
      } catch (err) {
        await user.delete().catch(() => {});
        throw err;
      }
    }
    return user.uid;
  } finally {
    await deleteApp(tempApp);
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// STUDENT — Accept coordinator-issued company assignment
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Accepts a coordinator-issued company assignment for a student.
 * The invite must exist in Firestore (created by coordinator) and not yet accepted.
 *
 * @param {string} inviteId — Firestore doc ID in companyInvites collection
 * @param {string} studentUid — the logged-in student's UID
 * @returns {Promise<void>}
 * @throws Error if invite not found, already accepted, or expired
 */
export const acceptCompanyInvite = async (inviteId, studentUid) => {
  const inviteRef = doc(db, "companyInvites", inviteId);
  const inviteSnap = await getDoc(inviteRef);

  if (!inviteSnap.exists()) {
    throw new Error("Invite not found.");
  }

  const inviteData = inviteSnap.data();

  // Check status
  if (inviteData.status === "accepted") {
    throw new Error("This invite has already been accepted.");
  }
  if (inviteData.status === "declined") {
    throw new Error("This invite has been declined.");
  }
  if (inviteData.status === "expired") {
    throw new Error("This invite has expired.");
  }

  // Mark invite accepted
  await updateDoc(inviteRef, {
    status: "accepted",
    acceptedBy: studentUid,
    acceptedAt: serverTimestamp(),
  });
};

// ─────────────────────────────────────────────────────────────────────────────
// COMPANY — Assignment query (count pending/accepted)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Fetches all student assignments (pending/accepted/declined) for a company.
 * Used by company dashboard to show the applicant list.
 *
 * @param {string} companyUid
 * @returns {Promise<object[]>} array of invite docs with status + student data
 */
export const getCompanyAssignments = async (companyUid) => {
  const q = query(collection(db, "companyInvites"), where("companyUid", "==", companyUid));
  const snap = await getDocs(q);

  // Enrich with student data
  const assignments = await Promise.all(
    snap.docs.map(async (d) => {
      const data = d.data();
      const studentSnap = await getDoc(doc(db, "students", data.studentUid));
      return {
        id: d.id,
        ...data,
        studentData: studentSnap.exists() ? studentSnap.data() : null,
      };
    })
  );

  return assignments;
};

/**
 * Counts assignments for a company by status.
 * Quick helper for dashboard stats.
 *
 * @param {string} companyUid
 * @returns {Promise<{ pending: number, accepted: number, declined: number }>}
 */
export const getCompanyAssignmentCounts = async (companyUid) => {
  const assignments = await getCompanyAssignments(companyUid);
  return {
    pending:  assignments.filter(a => a.status === "pending").length,
    accepted: assignments.filter(a => a.status === "accepted").length,
    declined: assignments.filter(a => a.status === "declined").length,
  };
};

// ─────────────────────────────────────────────────────────────────────────────
// COORDINATOR — Create account (by admin)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Creates a new coordinator account with a temporary password.
 * The account starts with no assigned departments — they must be added separately
 * (see assignCoordinatorDepartments, removeCoordinatorDepartment).
 *
 * The department is ALWAYS taken from the inviting coordinator's own
 * Firestore profile — never from the caller — so a coordinator can only
 * ever add accounts under their own department, regardless of what the
 * client sends.
 *
 * @param {object} accountData — { name, sex, contact, email, address, password }
 * @param {string} inviterUid — the currently logged-in coordinator's UID
 * @returns {Promise<string>} the new coordinator's UID
 */
export const createCoordinatorAccount = async (accountData, inviterUid) => {
  const { name, sex, contact, email, address, password } = accountData;
  const normalizedEmail = email.trim().toLowerCase();

  // Pull the inviter's current scope so the new coordinator shares it
  const inviterSnap = await getDoc(doc(db, "coordinators", inviterUid));
  const inviterData = inviterSnap.exists() ? inviterSnap.data() : {};

  // Check if this email already exists in coordinators collection.
  // Transferred-out accounts are now fully deleted (see transferCoordinatorAccount),
  // so any doc found here is a currently active coordinator — always a conflict.
  const dupSnap = await getDocs(query(collection(db, "coordinators"), where("email", "==", normalizedEmail)));
  if (!dupSnap.empty) {
    throw new Error("An active account with that email already exists.");
  }

  const newUid = await createAuthUserIsolated(normalizedEmail, password);

  await setDoc(doc(db, "coordinators", newUid), {
    uid: newUid,
    name: name.trim(),
    sex,
    contact,
    email: normalizedEmail,
    address,
    deptSelections: inviterData.deptSelections || [],
    assignedIndustries: inviterData.assignedIndustries || [],
    role: "coordinator",
    status: "active",
    passwordChanged: false,
    profileComplete: false,
    addedBy: inviterUid,
    createdAt: serverTimestamp(),
  });

  return newUid;
};

// ─────────────────────────────────────────────────────────────────────────────
// COORDINATOR — Transfer Account, invitation-based (hand off to a replacement
// coordinator via an emailed "Accept" link — the current account keeps full
// ─────────────────────────────────────────────────────────────────────────────
// COORDINATOR — invitation-based flows for Transfer Account and Add Account.
// Both hand off setup to the incoming coordinator via an emailed "Accept"
// link instead of the current coordinator choosing the new person's
// name/password directly. They share one Firestore collection
// (coordinatorInvites) and one set of Cloud Functions, distinguished by
// `type`:
//   - "transfer" — the CURRENT coordinator's account is removed once
//     accepted (see acceptCoordinatorInvite in functions/index.js).
//   - "add"      — a new, additional coordinator account is created; the
//     inviting coordinator keeps their own access throughout.
// ─────────────────────────────────────────────────────────────────────────────

// Random-enough token for the accept link — not a Firebase Auth credential,
// just a shared secret the emailed link carries so the accept screen (and
// the Cloud Functions behind it) can prove "this click came from that email"
// without needing the invite doc itself to be publicly readable.
const generateInviteToken = () => {
  const bytes = new Uint8Array(24);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
};

/**
 * Shared implementation behind initiateCoordinatorTransfer and
 * initiateCoordinatorAddition — re-confirms the current coordinator's
 * identity, then creates a pending invite doc. A Cloud Function
 * (sendCoordinatorInviteEmail) picks up the new doc and emails the incoming
 * coordinator an "Accept" link.
 */
const createCoordinatorInvite = async (type, currentUid, currentEmail, currentPassword, newCoordinatorEmail) => {
  // 1. Re-authenticate the current coordinator before doing anything.
  await signInWithEmailAndPassword(auth, currentEmail.trim().toLowerCase(), currentPassword);

  // 2. Load the current coordinator's scope to carry over to the invite.
  const currentSnap = await getDoc(doc(db, "coordinators", currentUid));
  if (!currentSnap.exists()) throw new Error("Current coordinator profile not found.");
  const currentData = currentSnap.data();

  const normalizedFromEmail = currentEmail.trim().toLowerCase();
  const normalizedToEmail   = newCoordinatorEmail.trim().toLowerCase();

  if (normalizedToEmail === normalizedFromEmail) {
    throw new Error("The new coordinator's email must be different from your own.");
  }

  // 3. The new email must not already belong to an active coordinator.
  const dupSnap = await getDocs(query(collection(db, "coordinators"), where("email", "==", normalizedToEmail)));
  if (!dupSnap.empty) {
    throw new Error("An active account with that email already exists.");
  }

  // 4. Cancel any earlier pending invites *of the same type* from this
  // coordinator so only the latest one is acceptable (avoids two valid
  // "Accept" links floating around for the same handoff/addition). A
  // pending "add" invite and a pending "transfer" invite from the same
  // coordinator are unrelated and can coexist.
  const pendingSnap = await getDocs(query(
    collection(db, "coordinatorInvites"),
    where("fromUid", "==", currentUid),
    where("type", "==", type),
    where("status", "==", "pending"),
  ));
  await Promise.all(pendingSnap.docs.map((d) => updateDoc(d.ref, { status: "cancelled" })));

  // 5. Create the invite — the Cloud Function trigger sends the email from here.
  const inviteRef = await addDoc(collection(db, "coordinatorInvites"), {
    type,
    fromUid:            currentUid,
    fromEmail:          normalizedFromEmail,
    fromName:           currentData.name || "",
    toEmail:            normalizedToEmail,
    deptSelections:     currentData.deptSelections || [],
    assignedIndustries: currentData.assignedIndustries || [],
    token:              generateInviteToken(),
    status:             "pending",
    createdAt:          serverTimestamp(),
  });

  return inviteRef.id;
};

/**
 * Starts a coordinator handoff — the CURRENT account is removed once the
 * invite is accepted. See createCoordinatorInvite for the shared mechanics.
 *
 * @param {string} currentUid
 * @param {string} currentEmail
 * @param {string} currentPassword — re-confirms identity before inviting
 * @param {string} newCoordinatorEmail
 * @returns {Promise<string>} the new invite's Firestore doc ID
 */
export const initiateCoordinatorTransfer = (currentUid, currentEmail, currentPassword, newCoordinatorEmail) =>
  createCoordinatorInvite("transfer", currentUid, currentEmail, currentPassword, newCoordinatorEmail);

/**
 * Invites an additional coordinator to join alongside the current one — the
 * inviting coordinator's own account and access are completely unaffected,
 * before or after the invite is accepted. See createCoordinatorInvite for
 * the shared mechanics.
 *
 * @param {string} currentUid
 * @param {string} currentEmail
 * @param {string} currentPassword — re-confirms identity before inviting
 * @param {string} newCoordinatorEmail
 * @returns {Promise<string>} the new invite's Firestore doc ID
 */
export const initiateCoordinatorAddition = (currentUid, currentEmail, currentPassword, newCoordinatorEmail) =>
  createCoordinatorInvite("add", currentUid, currentEmail, currentPassword, newCoordinatorEmail);

/**
 * Looks up a pending invite (transfer OR add) for the Accept screen —
 * validated server-side by the getCoordinatorInvite Cloud Function, which
 * checks the token before returning anything. The client never reads
 * coordinatorInvites directly via Firestore rules; this callable is the
 * only way to see an invite's details before it's accepted.
 *
 * @param {string} inviteId
 * @param {string} token
 * @returns {Promise<{ type: string, fromName: string, toEmail: string, deptSelections: object[] }>}
 */
export const getCoordinatorInvite = async (inviteId, token) => {
  const call = httpsCallable(functions, "getCoordinatorInvite");
  const { data } = await call({ inviteId, token });
  return data;
};

/**
 * Accepts a coordinator invite (transfer OR add): the incoming coordinator
 * chooses their own name + password here. Everything — creating the new
 * Auth account + Firestore profile, marking the invite accepted, and (for
 * type "transfer" only) removing the outgoing coordinator's doc — happens
 * server-side in the acceptCoordinatorInvite Cloud Function using Admin SDK
 * privileges.
 *
 * @param {string} inviteId
 * @param {string} token
 * @param {string} name
 * @param {string} password
 * @returns {Promise<{ uid: string }>}
 */
export const acceptCoordinatorInvite = async (inviteId, token, name, password) => {
  const call = httpsCallable(functions, "acceptCoordinatorInvite");
  const { data } = await call({ inviteId, token, name, password });
  return data;
};

// ─────────────────────────────────────────────────────────────────────────────
// Helper — Create auth user in isolation (sign out afterward)
// ─────────────────────────────────────────────────────────────────────────────